import React, { useState, useMemo, useEffect } from 'react';
import { PaymentMethod } from '../types/index';
import { valorPorMetodoNoMovimento, pagamentosDoMovimento } from '../shared/logica/pagamentos';
import SeletorData from '../paginas/caixa/SeletorData';
import CardFluxoSistema from '../paginas/caixa/CardFluxoSistema';
import CardConferenciaFisica from '../paginas/caixa/CardConferenciaFisica';
import TabelaHistoricoAuditoria from '../paginas/caixa/TabelaHistoricoAuditoria';
import { X, DollarSign, CheckCircle } from 'lucide-react';

/**
 * Página de Auditoria de Caixa (refatorada - dividida em componentes)
 */
export default function DailyControlView({ company, movements, logs, onSave, onDelete, currentUser }) {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [initialValueInput, setInitialValueInput] = useState(0);
  const [physicalValueInput, setPhysicalValueInput] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [manualNextDayValue, setManualNextDayValue] = useState(0);
  const [showManualInput, setShowManualInput] = useState(false);

  // Helper para arredondar valores para 2 casas decimais
  const roundTo2Decimals = (value) => {
    const num = Number(value) || 0;
    return Math.round(num * 100) / 100;
  };

  // Helper: converter datas/horas para Date de forma segura
  const safeToDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    // Firebase Timestamp pode vir aqui em alguns cenários (defensivo)
    if (typeof value?.toDate === 'function') {
      try {
        return value.toDate();
      } catch {
        return null;
      }
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  /**
   * Quanto dinheiro (CASH) este movimento adiciona numa data específica,
   * respeitando pagamento misto (paymentDate1/paymentDate2). (Reservado para uso futuro.)
   */
  // eslint-disable-next-line no-unused-vars
  const cashDoMovimentoNaData = (m, targetDate) => {
    if (!m || m.locationType !== 'NA_LOJA') return 0;

    // Pagamento misto: o CASH "acontece" em paymentDate1/paymentDate2 (se existir)
    if (m.isMixedPayment) {
      return pagamentosDoMovimento(m).reduce((total, p) => {
        const d = (p.paymentDate || m.date || '').split('T')[0];
        if (p.method === PaymentMethod.CASH && d === targetDate) {
          return total + p.value;
        }
        return total;
      }, 0);
    }

    // Pagamento simples
    return m.paymentMethod === PaymentMethod.CASH && m.date === targetDate ? Number(m.value || 0) : 0;
  };

  // Helper para somar apenas movimentos "Na Loja" com pagamento em dinheiro
  const somarCashNaLoja = (movimentos) => {
    return (movimentos || []).reduce((acc, m) => {
      // Filtrar apenas movimentos "Na Loja"
      if (m.locationType !== 'NA_LOJA') return acc;
      
      // Calcular o valor em dinheiro deste movimento
      return acc + valorPorMetodoNoMovimento(m, PaymentMethod.CASH);
    }, 0);
  };

  // Cálculo de entradas em dinheiro para o dia selecionado
  // APENAS movimentos "Na Loja" (locationType === 'NA_LOJA') com pagamento em dinheiro
  // Considera pagamentos mistos com datas diferentes (paymentDate1 e paymentDate2)
  const receivedCash = useMemo(() => {
    let total = 0;
    
    movements.forEach(m => {
      // Filtrar apenas movimentos "Na Loja"
      if (m.locationType !== 'NA_LOJA') return;
      
      if (m.isMixedPayment) {
        pagamentosDoMovimento(m).forEach(p => {
          const paymentDate = (p.paymentDate || m.date || '').split('T')[0];
          if (paymentDate === date && p.method === PaymentMethod.CASH) {
            total += p.value;
          }
        });
      } else {
        // Para pagamentos simples, verificar a data principal
        if (m.date === date && m.paymentMethod === PaymentMethod.CASH) {
          total += Number(m.value || 0);
        }
      }
    });
    
    return total;
  }, [movements, date]);

  // Dinheiro acumulado (é o mesmo que initialValueInput, mas formatado)
  const accumulatedCash = useMemo(() => {
    return Number(initialValueInput) || 0;
  }, [initialValueInput]);

  // Cálculo ajustado: sem subtrair paidOut
  const expectedFinal = Number(initialValueInput) + Number(receivedCash);
  const difference = Number(physicalValueInput) - expectedFinal;
  const isMatch = Math.abs(difference) < 0.01;

  // Reset fields when date changes to allow recalculation
  useEffect(() => {
    // Check if there's already a log for this date
    const logForDate = logs.find(log => log.date === date);
    if (logForDate) {
      // If log exists for this date, load its values (read-only mode)
      // Use initialValue (opening value for this day), not nextDayInitialValue (which is for tomorrow)
      setInitialValueInput(roundTo2Decimals(logForDate.initialValue || 0));
      setPhysicalValueInput(roundTo2Decimals(logForDate.physicalValue || 0));
    } else {
      // Reset to allow accumulation calculation for new record
      setInitialValueInput(0);
      setPhysicalValueInput(0);
    }
  }, [date, logs]);

  // Auto-preencher fundo de maneio: usar nextDayInitialValue do dia anterior
  useEffect(() => {
    // Soma dinheiro "Na Loja" após um fecho (closedAt) até antes da data selecionada.
    const somarCashAposFechoAteAntesDaData = (movs, lastLog) => {
      if (!lastLog?.date) return 0;
      const lastLogDate = lastLog.date;
      const closedAtDate = safeToDate(lastLog.closedAt);
      return (movs || []).reduce((acc, m) => {
        if (!m || m.locationType !== 'NA_LOJA') return acc;
        const createdAt = safeToDate(m.createdAt);
        const isAfterCloseOnSameDay = closedAtDate && createdAt ? createdAt.getTime() > closedAtDate.getTime() : true;
        const relevantDates = m.isMixedPayment
          ? pagamentosDoMovimento(m).map(p => ({
              d: (p.paymentDate || m.date || '').split('T')[0],
              amount: p.method === PaymentMethod.CASH ? p.value : 0
            }))
          : [{ d: m.date, amount: m.paymentMethod === PaymentMethod.CASH ? Number(m.value || 0) : 0 }];
        return acc + relevantDates.reduce((subAcc, part) => {
          if (!part?.d || !part.amount) return subAcc;
          if (part.d >= date) return subAcc;
          if (part.d > lastLogDate) return subAcc + part.amount;
          if (part.d === lastLogDate && isAfterCloseOnSameDay) return subAcc + part.amount;
          return subAcc;
        }, 0);
      }, 0);
    };

    if (initialValueInput === 0) {
      const sortedLogs = [...logs].sort((a, b) => {
        const dateA = a.date || '';
        const dateB = b.date || '';
        return dateB.localeCompare(dateA);
      });

      if (sortedLogs.length === 0) {
        const movementsBeforeDate = movements.filter(m => m.date < date);
        const accumulated = somarCashNaLoja(movementsBeforeDate);
        setInitialValueInput(roundTo2Decimals(accumulated));
      } else {
        const lastLogBeforeDate = sortedLogs.find(log => log.date < date);
        if (!lastLogBeforeDate) {
          const movementsBeforeDate = movements.filter(m => m.date < date);
          const accumulated = somarCashNaLoja(movementsBeforeDate);
          setInitialValueInput(roundTo2Decimals(accumulated));
        } else {
          const lastLogClosingValue = lastLogBeforeDate.nextDayInitialValue !== undefined
            ? Number(lastLogBeforeDate.nextDayInitialValue)
            : (Number(lastLogBeforeDate.physicalValue) || Number(lastLogBeforeDate.finalValue) || 0);
          const cashAfterClose = somarCashAposFechoAteAntesDaData(movements, lastLogBeforeDate);
          const total = roundTo2Decimals(lastLogClosingValue) + roundTo2Decimals(cashAfterClose);
          setInitialValueInput(roundTo2Decimals(total));
        }
      }
    }
  }, [logs, movements, date, initialValueInput]);

  const handleCloseDay = () => {
    // Check if a log already exists for this date
    const existingLog = logs.find(log => log.date === date);
    if (existingLog) {
      alert(`Já existe um registo de auditoria para a data ${date}. Não é possível criar um registo duplicado.`);
      return;
    }
    
    if (physicalValueInput === 0 && !window.confirm('Confirmar fecho com 0.00€ em caixa?')) return;
    
    // Reset modal state
    setManualNextDayValue(Number(physicalValueInput) || 0); // Pre-fill with physical value
    setShowManualInput(false);
    setShowConfirmModal(true);
  };

  const handleConfirmUsePhysical = () => {
    // Usar valor físico automaticamente
    handleConfirmCloseDay(Number(physicalValueInput) || 0);
  };

  const handleConfirmManual = () => {
    // Usar valor manual inserido
    handleConfirmCloseDay(Number(manualNextDayValue) || 0);
  };

  const handleConfirmCloseDay = (nextDayValue) => {
    setShowConfirmModal(false);
    setShowManualInput(false);
    
    const newAudit = { 
      date, 
      initialValue: Number(initialValueInput) || 0, 
      receivedCash: Number(receivedCash) || 0, 
      paidOutCash: 0,
      finalValue: Number(expectedFinal) || 0, 
      physicalValue: Number(physicalValueInput) || 0,
      nextDayInitialValue: Number(nextDayValue) || 0
    };

    onSave(newAudit);
    
    // Feedback e Reset
    setShowSuccess(true);
    setPhysicalValueInput(0);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleReset = () => {
    setInitialValueInput(0);
    setPhysicalValueInput(0);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-green-900 tracking-tight leading-none">Auditoria de Caixa</h1>
          <p className="text-green-600 font-bold mt-2 uppercase text-[10px] tracking-[0.2em]">
            Snapshot de conferência física • Rastreabilidade Total
          </p>
        </div>
        <SeletorData date={date} onDateChange={setDate} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <CardFluxoSistema
          initialValue={initialValueInput}
          onInitialValueChange={(val) => setInitialValueInput(roundTo2Decimals(val))}
          receivedCash={receivedCash}
          accumulatedCash={accumulatedCash}
          expectedFinal={expectedFinal}
          onReset={handleReset}
        />

        <CardConferenciaFisica
          physicalValue={physicalValueInput}
          onPhysicalValueChange={(val) => setPhysicalValueInput(Number(val) || 0)}
          isMatch={isMatch}
          difference={difference}
          showSuccess={showSuccess}
          onCloseDay={handleCloseDay}
        />
      </div>

      <TabelaHistoricoAuditoria logs={logs} onDelete={onDelete} currentUser={currentUser} />

      {/* Modal de Confirmação */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-[3rem] w-full max-w-md shadow-2xl p-8 md:p-10">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-3xl font-black text-slate-900 leading-none">
                Fechar o Dia
              </h2>
              <button 
                onClick={() => {
                  setShowConfirmModal(false);
                  setShowManualInput(false);
                }} 
                className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>

            {!showManualInput ? (
              // Pergunta inicial
              <div className="space-y-6">
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6">
                  <p className="text-sm font-bold text-emerald-800 uppercase tracking-widest mb-2">
                    Valor Físico Contado
                  </p>
                  <p className="text-4xl font-black text-emerald-900">
                    {Number(physicalValueInput).toFixed(2)}€
                  </p>
                </div>

                <p className="text-lg font-bold text-slate-700 leading-relaxed">
                  Deseja usar o valor físico contado ({Number(physicalValueInput).toFixed(2)}€) como <strong>Fundo de Maneio</strong> para o próximo dia?
                </p>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={handleConfirmUsePhysical}
                    className="flex-1 py-4 bg-emerald-600 text-white font-black rounded-2xl text-lg shadow-lg hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={20} />
                    Sim
                  </button>
                  <button
                    onClick={() => setShowManualInput(true)}
                    className="flex-1 py-4 bg-slate-200 text-slate-700 font-black rounded-2xl text-lg hover:bg-slate-300 transition-all"
                  >
                    Não
                  </button>
                </div>
              </div>
            ) : (
              // Campo manual
              <div className="space-y-6">
                <div>
                  <p className="text-lg font-bold text-slate-700 leading-relaxed mb-4">
                    Insira o valor para o <strong>Fundo de Maneio</strong> do próximo dia:
                  </p>
                  
                  <div className="bg-slate-100/50 p-6 rounded-3xl group transition-all focus-within:bg-white focus-within:shadow-inner border-2 border-slate-200">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-2">
                      Fundo de Maneio (Abertura) - Próximo Dia
                    </label>
                    <div className="flex items-center gap-3">
                      <DollarSign className="text-slate-300 group-focus-within:text-green-600" size={24}/>
                      <input 
                        type="number" 
                        step="0.01"
                        className="w-full bg-transparent text-3xl font-black outline-none text-slate-900" 
                        value={manualNextDayValue || ''} 
                        placeholder="0.00"
                        autoFocus
                        onChange={e => setManualNextDayValue(Number(e.target.value) || 0)} 
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={handleConfirmManual}
                    className="flex-1 py-4 bg-emerald-600 text-white font-black rounded-2xl text-lg shadow-lg hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={20} />
                    Confirmar
                  </button>
                  <button
                    onClick={() => setShowManualInput(false)}
                    className="flex-1 py-4 bg-slate-200 text-slate-700 font-black rounded-2xl text-lg hover:bg-slate-300 transition-all"
                  >
                    Voltar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
